import { ENotificationType, INotification } from "../interfaces";

const DEFAULT_TITLE = "Flownect";

const notificationTitles: Partial<Record<ENotificationType, string>> = {
  [ENotificationType.JobScheduled]: "Job scheduled",
  [ENotificationType.JobPunched]: "Job punched",
  [ENotificationType.JobRescheduled]: "Job rescheduled",
  [ENotificationType.JobCancelled]: "Job cancelled",
  [ENotificationType.JobInProgress]: "Job in progress",
  [ENotificationType.JobCompleted]: "Job completed",
  [ENotificationType.JobAssigned]: "New job assigned",
  [ENotificationType.JobAccepted]: "Job accepted",
  [ENotificationType.JobDeclined]: "Job declined",
  [ENotificationType.JobReminder]: "Job reminder",
  [ENotificationType.InvoicePaid]: "Invoice paid",
  [ENotificationType.InvoicePaidDeposit]: "Deposit invoice paid",
  [ENotificationType.QuoteAccepted]: "Quote accepted",
  [ENotificationType.AdminInvitationReceived]: "Admin invitation",
  [ENotificationType.InboundMessageReceived]: "New message",
  [ENotificationType.InternalMessageReceived]: "New message",
  [ENotificationType.InternalGroupMessageReceived]: "New group message",
  [ENotificationType.InternalGroupMentionReceived]: "You were mentioned",
};

const normalizeMeta = (meta: unknown): Record<string, unknown> => {
  if (!meta) return {};
  if (typeof meta === "string") {
    try {
      const parsed = JSON.parse(meta);
      return typeof parsed === "object" && parsed ? parsed : {};
    } catch {
      return {};
    }
  }

  return typeof meta === "object" ? (meta as Record<string, unknown>) : {};
};

const toText = (value: unknown): string => (typeof value === "string" ? value.trim() : "");

const getNotificationBody = (notification: INotification): string => {
  const meta = normalizeMeta(notification.meta);
  const metaMessage = toText(meta.message) || toText(meta.body) || toText(meta.text);

  return notification.message?.trim() || metaMessage || "You have a new notification.";
};

export const canShowBrowserNotification = (): boolean => {
  return (
    "Notification" in window &&
    window.isSecureContext &&
    Notification.permission === "granted" &&
    document.visibilityState !== "visible"
  );
};

interface ShowBrowserNotificationOptions {
  onClick?: () => void;
}

export const showBrowserNotification = (notification: INotification, options: ShowBrowserNotificationOptions = {}): void => {
  if (!canShowBrowserNotification()) return;

  const title = notificationTitles[notification.type] ?? DEFAULT_TITLE;
  const body = getNotificationBody(notification);

  const browserNotification = new Notification(title, {
    body,
    tag: `${notification.type}-${notification.id ?? body}`,
    data: {
      notificationId: notification.id,
      type: notification.type,
      meta: notification.meta,
    },
  });

  browserNotification.onclick = () => {
    window.focus();
    options.onClick?.();
    browserNotification.close();
  };
};
